import React, { useEffect, useRef, useState } from 'react';

import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

import { Box } from '../../../../../Box';
import { Typography } from '../../../../../Typography';
import { Skeleton } from '../../../../../Skeleton';
import { useHeight } from '../hooks';
import { ImageFailed } from '../../../ui';

interface ImagesGallerySliderProps {
  images: {
    src: string;
    name: string;
    id: string;
  }[];
  hasError: boolean;
  editable: boolean;
}

export const ImagesGallerySlider = ({
  images,
  editable,
  hasError = false,
}: ImagesGallerySliderProps) => {
  const { editorHeight, maxHeight } = useHeight();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<
    Record<string, { loading: boolean; fulfilled: boolean; rejected: boolean }>
  >({});
  const preloadCache = useRef<Record<string, boolean>>({});

  const staticHeight = editable ? editorHeight : maxHeight;

  // Предзагрузка изображений
  useEffect(() => {
    if (!images[currentIndex]?.src) return;

    const preloadImage = (url: string) => {
      if (preloadCache.current[url]) return;

      const img = new Image();
      img.src = url;
      preloadCache.current[url] = true;

      img.onloadstart = () => {
        setLoadedImages((prev) => ({
          ...prev,
          [url]: { loading: true, fulfilled: false, rejected: false },
        }));
      };
      img.onload = () => {
        setLoadedImages((prev) => ({
          ...prev,
          [url]: { fulfilled: true, loading: false, rejected: false },
        }));
      };
      img.onerror = () => {
        setLoadedImages((prev) => ({
          ...prev,
          [url]: { rejected: true, loading: false, fulfilled: false },
        }));
      };
    };

    // Предзагружаем текущее изображение
    preloadImage(images[currentIndex].src);
  }, [currentIndex, images]);

  const goToNext = (index: number) => {
    if (images[index]?.src) {
      setCurrentIndex(index);
    }
  };

  const getCurrentImage = (status?: {
    loading: boolean;
    fulfilled: boolean;
    rejected: boolean;
  }) => {
    if (status?.rejected) {
      return <ImageFailed height={staticHeight} variant="one" />;
    }

    if (status?.fulfilled) {
      return (
        <img
          src={images[currentIndex].src}
          alt={`Slide ${currentIndex}`}
          style={{
            maxWidth: '100%',
            height: staticHeight,
            objectFit: 'contain',
          }}
          loading="lazy"
        />
      );
    }

    return <Skeleton height={staticHeight} width="100%" variant="rect" />;
  };

  if (!Object.keys(loadedImages).length) {
    return <Skeleton height={staticHeight} width="100%" variant="rect" />;
  }

  if (hasError) {
    return <ImageFailed height={staticHeight} variant="many" />;
  }

  return (
    <Box
      position="relative"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bgcolor={
        loadedImages[images[currentIndex].src]
          ? 'bg.dark.primary'
          : 'bg.light.primary'
      }
      radius="m"
      overflow="hidden"
    >
      {getCurrentImage(loadedImages[images[currentIndex].src])}

      <Box
        position="absolute"
        top="calc(50% - 16px)"
        left={20}
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={32}
        height={32}
        radius="round"
        bgcolor="bg.dark.secondary"
        color="text.dark.primary"
        cursor={currentIndex === 0 ? 'default' : 'pointer'}
        onClick={() => goToNext(currentIndex - 1)}
      >
        <ChevronLeftIcon color="inherit" />
      </Box>
      <Box
        position="absolute"
        top="calc(50% - 16px)"
        right={20}
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={32}
        height={32}
        radius="round"
        bgcolor="bg.dark.secondary"
        color="text.dark.primary"
        cursor={currentIndex === images.length - 1 ? 'default' : 'pointer'}
        onClick={() => goToNext(currentIndex + 1)}
      >
        <ChevronRightIcon color="inherit" />
      </Box>

      <Box
        position="absolute"
        bottom={16}
        left="50%"
        display="flex"
        alignItems="center"
        gap="2"
        px="8"
        py="2"
        transform="translateX(-50%)"
        radius="l"
        bgcolor="bg.dark.secondary"
      >
        <Typography variant="overline" color="text.dark.primary">
          {currentIndex + 1}
        </Typography>
        <Typography variant="overline" color="text.dark.primary">
          /
        </Typography>
        <Typography variant="overline" color="text.dark.primary">
          {images.length}
        </Typography>
      </Box>
    </Box>
  );
};
